import { Close, CloseRounded, Image, ImageRounded, PhotoLibraryRounded } from "@mui/icons-material";
import { Dialog, DialogTitle, Stack, Button, Breadcrumbs, Typography, IconButton, DialogContent, SxProps, Theme, Card, CardMedia, ImageList, ImageListItem, Divider, DialogActions, styled, Chip, Box, LinearProgress } from "@mui/material";
import { FullMetadata, getBlob, getDownloadURL, getMetadata, getStorage, listAll, ref, StorageReference, uploadBytes, uploadBytesResumable } from "firebase/storage";
import { useEffect, useRef, useState } from "react";
import { getErrorString } from "../utils/getErrorString";
import { UploadImage } from "../types/UploadImage";
import { collection, doc, getDocs, getFirestore, setDoc } from "firebase/firestore";
import { v4 as uuidv4 } from 'uuid';
import { DateTime } from "luxon";

const ImageBrowserDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
        'padding-top': theme.spacing(0),
        'padding-bottom': theme.spacing(0),
    
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(2),
        'padding-top': theme.spacing(1),
        'padding-bottom': theme.spacing(1),
    },
}));

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

export type ImageInfo = {
    key: string,
    ref: StorageReference;
    blob: Blob;
    // metadata: FullMetadata;
}

export type ImageBrowserProps = {
    path: string,
    collectionPath: string,
    open: boolean,
    multiple?: boolean,
    onSelected?: (imageInfos: UploadImage[]) => void,
    onClose: () => void;
}
export const ImageBrowser = (props: ImageBrowserProps) => {
    const { path, collectionPath, open, onSelected, onClose } = props;
    const multiple = props.multiple === true;
    const segments = path.split("/");

    const [uploadFiles, setUploadFiles] = useState<FileList | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const [imageInfos, setImageInfos] = useState<UploadImage[]>([]);
    const [selectedImageInfos, setSelectedImageInfos] = useState<UploadImage[]>([]);
    const [error, setError] = useState('');

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleOnFileUploadItemRemoved = (newFileList: FileList) => {
        if (fileInputRef.current !== null) {
            if (newFileList.length === 0) {
                fileInputRef.current.value = "";
                setUploadFiles(null);
            } else {
                fileInputRef.current.files = newFileList;
                setUploadFiles(newFileList);
            }
        }
    };

    const handleUploadOnClick = () => {
        if (uploadFiles && uploadFiles.length > 0 && !uploading) {
            setUploading(true);
            setUploadProgress(0);

            let totalSize = 0;
            let uploadedSize = 0;
            let worker: Promise<UploadImage>[] = [];
            for (let i = 0; i < uploadFiles.length; i++) {
                totalSize += uploadFiles[i].size;
                const iPromiseYou = new Promise<UploadImage>((resolve, reject) => {
                    const id = uuidv4();
                    const file = uploadFiles[i];

                    const storage = getStorage();
                    const storageRef = ref(storage, `${path}/${id}.${file.name.split(".").pop()}`);

                    const uploadTask = uploadBytesResumable(storageRef, file);
                    uploadTask.on(
                        'state_changed',
                        (snapshot) => {
                            uploadedSize += snapshot.bytesTransferred;
                            setUploadProgress((uploadedSize / totalSize) * 100);
                        },
                        (error) => {
                            reject(error);
                        },
                        () => {
                            const snapshot = uploadTask.snapshot;
                            getDownloadURL(snapshot.ref)
                                .then(async downloadUrl => {
                                    const upload: UploadImage = {
                                        id: id,
                                        fullPath: snapshot.metadata.fullPath,
                                        downloadUrl: downloadUrl,
                                        contentType: snapshot.metadata.contentType ?? "",
                                        dateUploaded: DateTime.now().toMillis()
                                    };
            
                                    const db = getFirestore();
                                    const ref = doc(db, collectionPath, upload.id);
                                    await setDoc(ref, upload);
                                    resolve(upload);
                                })
                                .catch(error => {
                                    const reasonString = getErrorString(error, "An error has occured.")
                                });
                        }
                    );
                });
                worker.push(iPromiseYou);
            }

            Promise.allSettled<UploadImage>(worker)
                .finally(() => {
                    setUploading(false);
                    setUploadProgress(0);
                });
        }
    };

    const handleImageOnClick = (imageInfo: UploadImage) => {
        // Check if image is already selected
        if (multiple) {
            if (selectedImageInfos.includes(imageInfo)) {
                setSelectedImageInfos(selectedImageInfos.filter(v => v !== imageInfo));
            } else {
                setSelectedImageInfos([ ...selectedImageInfos, imageInfo ]);
            }
        } else {
            if (selectedImageInfos.length > 0 && selectedImageInfos[0] === imageInfo) {
                setSelectedImageInfos([]);
            } else {
                setSelectedImageInfos([imageInfo]);
            }
        }
    };

    const handleOnSelectOnClick = () => {
        onSelected && onSelected(selectedImageInfos);
    };
    
    useEffect(() => {
        const db = getFirestore();

        const query = collection(db, collectionPath);

        getDocs(query)
            .then((qSnap) => {
                setImageInfos(
                    qSnap.docs.map(dSnap => {
                        return dSnap.data() as UploadImage;
                    })
                );
            });
    }, []);

    return (
        <ImageBrowserDialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle id="alert-dialog-title">
                <Stack direction="row" spacing={2} alignItems="center">
                    <Breadcrumbs aria-label="breadcrumb" sx={{ flexGrow: 1 }}>
                        {segments.map((segment, index) => {
                            let sx: SxProps<Theme> = {
                                display: 'flex',
                                alignItems: 'center'
                            };

                            if (index === segments.length - 1) {
                                sx = { ...sx, color: 'text.primary' }
                            }

                            return (
                                <Typography key={segment} sx={ sx }>
                                    {segment}
                                </Typography>
                            );
                        })}
                    </Breadcrumbs>
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={(theme) => ({ color: theme.palette.grey[500] })}
                    >
                        <Close />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Stack direction="row" spacing={2}>
                    <Stack flex={1}>
                        <Stack>
                            <Typography>IMAGE</Typography>
                        </Stack>
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                    <Stack flex={3} py={2}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Stack direction="row" flexGrow={1} spacing={1} alignItems="center">
                                <UploadFilesPreview uploadFiles={uploadFiles} onFileRemove={handleOnFileUploadItemRemoved} />
                            </Stack>
                            <Button component="label" disabled={uploading} role={undefined} variant="text">
                                BROWSE...
                                <VisuallyHiddenInput
                                    ref={fileInputRef}
                                    type="file"
                                    onChange={(event) => setUploadFiles(event.target.files)}
                                    accept="image/jpg, image/jepg, image/png"
                                    multiple
                                />
                            </Button>
                            <Button disabled={uploadFiles === null || uploading} onClick={handleUploadOnClick}>
                                UPLOAD
                            </Button>
                        </Stack>
                        {uploading && (
                            <Box flex={1} mt={1}>
                                <LinearProgress variant="determinate" value={uploadProgress} />
                            </Box>
                        )}
                        {error.trim().length === 0 && (
                            <Typography>{error}</Typography>
                        )}
                        <ImageList cols={3} rowHeight={164}>
                            {imageInfos.map((imageInfo) => {
                                return (
                                    <ImageBrowserImage 
                                        key={imageInfo.id} 
                                        imageInfo={imageInfo} 
                                        isSelected={selectedImageInfos.includes(imageInfo)}
                                        onClick={handleImageOnClick} 
                                    />
                                );
                            })}
                        </ImageList>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button disabled={selectedImageInfos.length === 0} onClick={handleOnSelectOnClick}>
                    SELECT
                </Button>
            </DialogActions>
        </ImageBrowserDialog>
    );
};

export const ImageBrowserImage = (props: { imageInfo: UploadImage, isSelected?: boolean, onClick?: (imageInfo: UploadImage) => void }) => {
    const { imageInfo, isSelected, onClick } = props;

    return (
        <ImageListItem 
            sx={{
                cursor: 'pointer'
            }}
            style={{
                ...(isSelected ? { border: "5px solid black" } : {})
            }}
            onClick={() => onClick && onClick(imageInfo)}
        >
            <img alt={imageInfo.id} src={imageInfo.downloadUrl} />
        </ImageListItem>
    )
};

export const UploadFilesPreview = (props: {uploadFiles: FileList | null; onFileRemove?: (newList: FileList) => void}) => {
    const { uploadFiles, onFileRemove } = props;

    if (uploadFiles === null) {
        return (
            <Typography variant="caption">
                Click browse to upload a files.
            </Typography>
        );
    }

    const handleDelete = (indexToDelete: number) => {
        const dataTransfer = new DataTransfer();

        for (let i = 0; i < uploadFiles.length; i++) {
            const file = uploadFiles.item(i);
            if (i !== indexToDelete) {
                if (file !== null) {
                    dataTransfer.items.add(file);
                }
            }
        }

        onFileRemove && onFileRemove(dataTransfer.files);
    };

    let icon: JSX.Element | null = null;
    if (uploadFiles.length > 1) {
        icon = <PhotoLibraryRounded />
    } else {
        icon = <ImageRounded />
    }

    return (
        <>
            {icon}
            {[...Array(uploadFiles.length)].map((a, i) => {
                const file = uploadFiles.item(i);
                return (
                    <Chip key={i} label={file?.name} onDelete={() => handleDelete(i)} />
                );
            })}
        </>
    );
};